import React, { FunctionComponent, useContext, useEffect } from 'react';
import { useTranslation } from '@wix/wix-i18n-config';
import css from './index.module.scss';
import { Box, Button, Heading, Modal, Text } from '@wix/design-system';
import { useRouter } from 'next/router';
import { Context } from '../../context';
import { isMobile, isTablet } from '../../utils/userAgent';
import { RoadmapFeatureRequestModal } from '../RoadmapFeatureRequestModal';
import { useHttpClient } from '@wix/fe-essentials-standalone';
import { ALL_PRODUCTS_LABEL_ID, BI } from '../../constants';
import {
  pageClick,
  roadmapWishlistFormClick,
} from '@wix/bi-logger-new-help-center/v2';
import { useBI } from '../../hooks/useBI';
import { DATA_HOOKS } from '../../dataHooks';
import { pushUrl } from '../InnerLink';
import { Article, SourceType } from '@wix/answers-api';
import { tabToName } from '../../utils/roadmap';

type RoadmapHeaderProps = {
  isArticlePage?: boolean;
  roadmapLabels?: {
    id: string;
    name: string;
  }[];
  selectedLabelId?: string;
};

export const RoadmapHeader: FunctionComponent<RoadmapHeaderProps> = ({
  isArticlePage,
  selectedLabelId,
  roadmapLabels,
}) => {
  const { t } = useTranslation();
  const context = useContext(Context);
  const { query } = useRouter();
  const { locale } = useRouter();
  const { isWixStudioUser } = useContext(Context);
  const isMobileView = isMobile(context);
  const isTabletView = isTablet(context);
  const router = useRouter();
  const { sendBIEvent } = useBI();
  const { showRequestFeature, origin } = query;
  const { answersApi } = useContext(Context);

  const goToRoadmap = () => router.push('/roadmap');
  const isMidSizeScreen = isTabletView || isArticlePage;

  const midOrNormalClassName = `${css.backgroundWrapper} ${
    isMidSizeScreen ? css.midSize : ''
  }`;
  const httpClient = useHttpClient();

  useEffect(() => {
    if (showRequestFeature && isWixStudioUser) {
      void sendBIEvent(
        roadmapWishlistFormClick({
          source_name: BI.SOURCE_NAMES.ROADMAP,
          clicked_item_type: BI.CLICKED_ITEM_TYPES.EXTERNAL_FORM_LINK,
          kb_lang: locale as string,
          origin_url: encodeURIComponent(document.referrer),
          origin: origin?.toString(),
        })
      );
    }
  }, []);

  const setShowFeatureRequestModal = async (show: boolean) => {
    if (isWixStudioUser) {
      const newUrl = new URL(window.location.href);
      if (show) {
        newUrl.searchParams.set('showRequestFeature', 'true');
      } else {
        newUrl.searchParams.delete('showRequestFeature');
        newUrl.searchParams.delete('origin');
      }
      await pushUrl(`${newUrl.pathname}${newUrl.search}`, true);
    }
  };

  const onSendFR = async (
    productName: string,
    description: string,
    impact: string,
    suggestedArticle?: Article
  ) => {
    await createMondayFeatureRequest(
      productName,
      description,
      impact,
      suggestedArticle
    );
  };
  const createMondayFeatureRequest = async (
    productName: string,
    description: string,
    featureImpact: string,
    suggestedArticle?: Article
  ) => {
    const date = new Date().toISOString();
    const formattedDate = date.slice(0, date.indexOf('T'));

    const dto = {
      product: productName,
      featureDescription: description,
      featureImpact,
      date: formattedDate,
    };

    await httpClient.post<string>('/api/monday/featureRequest', dto);
    await sendBIEvent(
      roadmapWishlistFormClick({
        form_step: suggestedArticle
          ? BI.WISHLIST_FORM.ALTERNATIVE_SUGGESTER
          : BI.WISHLIST_FORM.MAIN,
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: suggestedArticle
          ? BI.CLICKED_ITEM_TYPES.WISHLIST.SEND_AFTER_SUGGESTED
          : BI.CLICKED_ITEM_TYPES.SEND,
        kb_lang: locale as string,
        product: productName,
        feature_description: description,
        feature_impact: featureImpact,
        chosen_alternative_id: suggestedArticle
          ? suggestedArticle.id
          : undefined,
        chosen_alternative_resolution: suggestedArticle?.resolution
          ? tabToName(suggestedArticle.resolution)
          : undefined,
      })
    );
  };

  const onClickFeatureRequest = async () => {
    await setShowFeatureRequestModal(true);
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: 'request_a_feature',
        clicked_text: 'request_a_feature',
        kb_lang: locale as string,
        item_id:
          selectedLabelId && selectedLabelId !== ALL_PRODUCTS_LABEL_ID
            ? selectedLabelId
            : undefined,
      })
    );
  };

  const onClose = async () => {
    await setShowFeatureRequestModal(false);
    await sendBIEvent(
      roadmapWishlistFormClick({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.CLOSE,
        kb_lang: locale as string,
      })
    );
  };

  const className = isMobileView
    ? css.backgroundWrapperMobile
    : midOrNormalClassName;

  const onVote = async (articleId: string) => {
    await answersApi.articles.voteOnIssueOrFeature({
      sourceId: null,
      locale: locale as string,
      id: articleId,
      sourceType: SourceType.HELP_CENTER,
    });
    localStorage?.setItem(`${articleId}${locale}`, 'true');
  };
  return (
    <div onClick={isArticlePage ? goToRoadmap : undefined}>
      <Box padding={isMobileView ? '0 15px' : '0 36px'} className={className}>
        {isMobileView ? (
          <Box
            dataHook={DATA_HOOKS.ROADMAP_HEADER}
            verticalAlign="middle"
            className={css.roadmapMobileHeader}
          >
            <Text
              weight="bold"
              light={isMobileView}
              className={css.roadmapMobileHeaderTitle}
            >
              {t('roadmap.breadcrumbs')}
            </Text>
          </Box>
        ) : (
          <Box
            dataHook={DATA_HOOKS.ROADMAP_HEADER}
            width="100%"
            verticalAlign="middle"
            direction="vertical"
            align="left"
            className={`${css.roadmapHeader} ${
              isArticlePage ? css.clickable : ''
            }`}
          >
            <Heading className={css.roadMapHeading} appearance="H3">
              {t('roadmap.page.heading')}
            </Heading>
            {!isTabletView && !isArticlePage && (
              <Text className={css.roadMapSubHeading}>
                {t('roadmap.page.sub-heading')}
              </Text>
            )}
          </Box>
        )}
        {!isArticlePage && isWixStudioUser && !isMobileView && (
          <Button
            className={css.requestFeatureButton}
            onClick={onClickFeatureRequest}
            priority="secondary"
            skin="dark"
          >
            {t('roadmap.feature-request')}
          </Button>
        )}
      </Box>
      {showRequestFeature && isWixStudioUser && roadmapLabels && (
        <Modal
          isOpen={!!showRequestFeature}
          onRequestClose={onClose}
          shouldCloseOnOverlayClick={false}
          screen="desktop"
          shouldDisplayCloseButton
          dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_MODAL}
        >
          <RoadmapFeatureRequestModal
            labels={roadmapLabels}
            onSend={onSendFR}
            onClose={() => setShowFeatureRequestModal(false)}
            onVote={onVote}
          />
        </Modal>
      )}
    </div>
  );
};
